import { useEffect, useState, type CSSProperties } from "react";
import { functionTabShellStyle, tabLabelStyle } from "@/components/common/tab-shell";
import { getFinanceRates, type FinanceRate } from "@/lib/api";
import {
	BILLING_EVENT_TYPE_LABELS,
	formatUnitRate,
	groupRatesByTab,
	type BillingRateCategory,
} from "@/lib/billing";
import { formatMarginRatio, grossMargin } from "@/lib/finance";

/**
 * 管理ページ (/admin) の「財務」タブ (SuperAdmin のみ)。単価は顧客向けの単価表・利用ガイドの
 * 料金ページと同じ API の規則で解決されたものを、原価・粗利と並べて見せる。
 */
export function FinancePage() {
	const [rates, setRates] = useState<FinanceRate[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [activeTabId, setActiveTabId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		getFinanceRates()
			.then((data) => {
				if (!cancelled) setRates(data);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const tabs = rates === null ? [] : groupRatesByTab(rates);
	const activeTab = tabs.find((t) => t.tab.id === activeTabId) ?? tabs[0];

	return (
		<div
			data-testid="finance-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2
				style={{
					margin: 0,
					fontSize: "var(--text-title)",
					fontWeight: 600,
					letterSpacing: "var(--tracking-snug)",
					color: "var(--fg-1)",
				}}
			>
				単価表
			</h2>
			<p
				style={{
					margin: "6px 0 16px",
					fontSize: "var(--text-caption)",
					lineHeight: "var(--leading-normal)",
					color: "var(--fg-3)",
				}}
			>
				いま適用されている単価と原価です (1 クレジット = 1 円)。粗利率は (単価 − 原価) ÷ 単価です。
			</p>

			{error !== null && (
				<p
					data-testid="finance-rates-error"
					style={{ margin: 0, fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{error === null && rates === null && (
				<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					読み込み中…
				</p>
			)}

			{tabs.length > 0 && (
				<div
					style={{
						display: "flex",
						height: 36,
						marginBottom: 20,
						borderBottom: "var(--pane-header-border)",
					}}
				>
					{tabs.map(({ tab }) => (
						<button
							key={tab.id}
							type="button"
							data-testid={`finance-rates-tab-${tab.id}`}
							onClick={() => setActiveTabId(tab.id)}
							style={{ ...functionTabShellStyle(tab.id === activeTab?.tab.id), padding: "0 12px" }}
						>
							<span style={tabLabelStyle}>{tab.label}</span>
						</button>
					))}
				</div>
			)}

			{activeTab !== undefined && (
				<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
					{activeTab.categories.map((c) => (
						<RateCategoryTable key={c.category.id} rateCategory={c} />
					))}
				</div>
			)}
		</div>
	);
}

const NUMERIC_COLUMNS = ["単価", "原価", "粗利", "粗利率"] as const;

const headerCellStyle: CSSProperties = {
	padding: "8px 12px",
	color: "var(--fg-3)",
	fontWeight: 600,
	whiteSpace: "nowrap",
	borderBottom: "var(--pane-header-border)",
};

const cellStyle: CSSProperties = {
	padding: "6px 12px",
	color: "var(--fg-1)",
	borderTop: "1px solid var(--line-1)",
};

const numericCellStyle: CSSProperties = {
	...cellStyle,
	textAlign: "right",
	whiteSpace: "nowrap",
	fontVariantNumeric: "tabular-nums",
};

/** カテゴリ 1 件分の単価表。内訳 (モデルなど) ごとに行をまとめ、内訳名は先頭の行にだけ出す。 */
function RateCategoryTable({ rateCategory }: { rateCategory: BillingRateCategory<FinanceRate> }) {
	const { category, groups } = rateCategory;
	const hasVariants = groups.some((g) => g.label !== null && g.label !== "");
	return (
		<section data-testid={`finance-rates-category-${category.id}`}>
			<h3
				style={{
					margin: "0 0 8px",
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					letterSpacing: "var(--tracking-heading)",
					color: "var(--fg-2)",
				}}
			>
				{category.label}
			</h3>
			<div
				style={{
					boxShadow: "var(--shadow-ring)",
					borderRadius: "var(--radius-4)",
					overflow: "hidden",
				}}
			>
				<table
					className="text-caption"
					style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-caption)" }}
				>
					<thead>
						<tr style={{ background: "var(--surface-2)" }}>
							{hasVariants && <th style={{ ...headerCellStyle, textAlign: "left" }}>内訳</th>}
							<th style={{ ...headerCellStyle, textAlign: "left" }}>メータ</th>
							{NUMERIC_COLUMNS.map((label) => (
								<th key={label} style={{ ...headerCellStyle, textAlign: "right" }}>
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="select-text">
						{groups.flatMap((group) =>
							group.rates.map((rate, i) => (
								<RateRow
									key={`${group.key}:${rate.eventType}`}
									rate={rate}
									variantLabel={hasVariants ? (i === 0 ? (group.label ?? "") : "") : null}
								/>
							)),
						)}
					</tbody>
				</table>
			</div>
		</section>
	);
}

/** 単価 1 行。variantLabel が null なら内訳の列自体を出さない。 */
function RateRow({ rate, variantLabel }: { rate: FinanceRate; variantLabel: string | null }) {
	const price = formatUnitRate(rate.eventType, rate.unitPriceCredits);
	const { profit, ratio } = grossMargin(rate);
	const negative = profit !== null && profit < 0;
	return (
		<tr data-testid={`finance-rate-${rate.variantKey ?? "all"}-${rate.eventType}`}>
			{variantLabel !== null && <td style={{ ...cellStyle, fontWeight: 600 }}>{variantLabel}</td>}
			<td style={cellStyle}>{BILLING_EVENT_TYPE_LABELS[rate.eventType] ?? rate.eventType}</td>
			<td style={numericCellStyle}>
				{price.credits}
				<span style={{ marginLeft: 4, color: "var(--fg-3)" }}>{price.unit}</span>
			</td>
			<td style={numericCellStyle}>
				{rate.unitCostCredits === null ? (
					<span style={{ color: "var(--fg-3)" }}>未設定</span>
				) : (
					formatUnitRate(rate.eventType, rate.unitCostCredits).credits
				)}
			</td>
			<td style={{ ...numericCellStyle, color: negative ? "var(--danger)" : cellStyle.color }}>
				{profit === null ? "—" : formatUnitRate(rate.eventType, profit).credits}
			</td>
			<td
				data-testid="finance-rate-margin"
				style={{ ...numericCellStyle, color: negative ? "var(--danger)" : cellStyle.color }}
			>
				{ratio === null ? "—" : formatMarginRatio(ratio)}
			</td>
		</tr>
	);
}
